import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  themeQuartz,
  ColDef,
  ICellRendererParams,
  GridReadyEvent,
} from "ag-grid-community";
import { X, ArrowLeft, Eye, EyeOff, Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../common/axiosInstance";
import { DotLoader } from "../../components/loader/Loader";
import { usePremisesStore } from "../../store/store";
import { getPremiseName, getMonthName, getReportTypeConfig } from "./constants";

// Sub-components (no router dependency)
import ExternalAuditReportTable from "../../components/KPIScoreCard/ExternalAuditReportTable";
import InternalAuditReportTable from "../../components/KPIScoreCard/InternalAuditReportTable";
import FireSafetyParametersTable from "../../components/KPIScoreCard/FireSafetyParametersTable";
import AuditReportChart from "../../components/KPIScoreCard/AuditReportChart";
import FireSafetyParametersChart from "../../components/KPIScoreCard/FireSafetyParametersChart";
import ElectricalSafetyTable, {
  ElectricalSafetySection,
} from "../../components/ElectricalSafetyScorecard/ElectricalSafetyTable";
import ElectricalSafetyChart from "../../components/ElectricalSafetyScorecard/ElectricalSafetyChart";
import SplitACChecklistTable, {
  SplitACChecklistRow,
} from "../../components/SplitACChecklist/SplitACChecklistTable";
import FLSSafetySystemsTable, {
  FLSSafetySystemRow,
} from "../../components/MISChecklist/FLSSafetySystemsTable";
import MEPFLSInfrastructureTable, {
  MEPFLSInfrastructureRow,
} from "../../components/MISChecklist/MEPFLSInfrastructureTable";
import FireIncidentDrillsTable, {
  FireIncidentDrillsRow,
} from "../../components/MISChecklist/FireIncidentDrillsTable";
import ERTSafetyEquipmentTable, {
  ERTSafetyEquipmentRow,
} from "../../components/MISChecklist/ERTSafetyEquipmentTable";
import IncidentSummaryTable, {
  IncidentSummaryRow,
} from "../../components/MISChecklist/IncidentSummaryTable";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d: string) => {
  if (!d) return "N/A";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return "N/A"; }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  let cls = "bg-gray-100 text-gray-700";
  if (["submitted","completed","active","pass"].includes(s)) cls = "bg-green-100 text-green-800";
  else if (["pending","in progress","draft","in_progress"].includes(s)) cls = "bg-yellow-100 text-yellow-800";
  else if (["failed","rejected","inactive"].includes(s)) cls = "bg-red-100 text-red-800";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {status || "N/A"}
    </span>
  );
};

// ─── InfoRow helper ───────────────────────────────────────────────────────────
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
    <p className="text-gray-800 font-medium mt-0.5 text-sm">{value}</p>
  </div>
);

// ─── 1. Fire Safety Scorecard ─────────────────────────────────────────────────
const FireSafetyScorecardView: React.FC<{ id: string; onClose: () => void }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAuditChart, setShowAuditChart] = useState(false);
  const [showParamsChart, setShowParamsChart] = useState(false);

  useEffect(() => {
    axiosInstance.get(`/api/fire-safety-scorecards/${id}`)
      .then(r => setData(r.data?.data || r.data))
      .catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownload = () => {
    if (!data) return;
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(18); doc.setTextColor(0, 168, 165);
      doc.text("Fire Safety Scorecard Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      doc.text(`Name: ${data.name || "N/A"}`, 14, 26);
      doc.text(`Premises: ${data.premisesName || "N/A"}`, 14, 32);
      doc.text(`Date: ${data.date ? fmt(data.date) : "N/A"}`, 14, 38);

      const ext = data.externalAuditRecords || [];
      const int_ = data.internalAuditRecords || [];
      const params = data.fireSafetyParameters || [];

      doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(255,100,0);
      doc.rect(14, 44, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("EXTERNAL AUDIT REPORT", 16, 50);
      autoTable(doc, {
        startY: 54,
        head: [["Category","Total NC","Completion NC","Pending","Completion %","Current Score","Max Score"]],
        body: ext.map((r: any) => [`Cat ${r.category}`, r.totalNC ?? "N/A", r.completionNC ?? "N/A", r.pending ?? "N/A", r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A", r.currentScore != null ? r.currentScore.toFixed(2) : "N/A", r.maxScore]),
        styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [255,100,0] },
      });
      let y = (doc as any).lastAutoTable.finalY + 8;

      doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(180,0,120);
      doc.rect(14, y, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("INTERNAL AUDIT REPORT", 16, y + 6); y += 10;
      autoTable(doc, {
        startY: y,
        head: [["Category","Total NC","Completion NC","Pending","Completion %","Current Score","Max Score"]],
        body: int_.map((r: any) => [`Cat ${r.category}`, r.totalNC ?? "N/A", r.completionNC ?? "N/A", r.pending ?? "N/A", r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A", r.currentScore != null ? r.currentScore.toFixed(2) : "N/A", r.maxScore]),
        styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [180,0,120] },
      });
      y = (doc as any).lastAutoTable.finalY + 8;

      doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(0,168,165);
      doc.rect(14, y, doc.internal.pageSize.width - 28, 8, "F");
      doc.text("PARAMETERS OF FIRE SAFETY", 16, y + 6); y += 10;
      const ts = params.reduce((s: number, p: any) => s + (p.score ?? 0), 0);
      const tm = params.reduce((s: number, p: any) => s + p.maxScore, 0);
      autoTable(doc, {
        startY: y,
        head: [["Parameter","In %","Score","Max Score","Percentage"]],
        body: [...params.map((p: any) => [p.parameter, p.inPercent != null ? p.inPercent.toFixed(2) : "N/A", p.score != null ? p.score.toFixed(2) : "N/A", p.maxScore.toFixed(2), p.percentage != null ? `${p.percentage.toFixed(2)}%` : "N/A"]),
          ["TOTAL","",ts.toFixed(2),tm.toFixed(2), tm > 0 ? `${((ts/tm)*100).toFixed(2)}%` : "N/A"]],
        styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [0,168,165] },
        didParseCell: (d: any) => { if (d.row.index === params.length) { d.cell.styles.fontStyle = "bold"; d.cell.styles.fillColor = [255,235,150]; } },
      });
      doc.save(`Fire_Safety_Scorecard_${(data.name || "Report").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
    } catch { alert("Failed to generate PDF."); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><DotLoader /></div>;
  if (error || !data) return <div className="p-6 text-red-600 text-sm">{error || "No data"}</div>;

  return (
    <div className="w-full px-6 py-6">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{data.name || "Fire Safety Scorecard"}</h2>
          <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium shadow-sm">
            <Download className="w-4 h-4" />Download PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow label="Premises" value={data.premisesName || "N/A"} />
          <InfoRow label="Date" value={data.date ? fmt(data.date) : "N/A"} />
          <InfoRow label="Description" value={data.description || "—"} />
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-800">External Audit Report</h3>
            <button onClick={() => setShowAuditChart(!showAuditChart)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-xs font-medium">
              {showAuditChart ? <><EyeOff className="w-3.5 h-3.5" />Hide Chart</> : <><Eye className="w-3.5 h-3.5" />Show Chart</>}
            </button>
          </div>
          {showAuditChart && <div className="mb-4 border-t pt-4"><AuditReportChart externalRecords={data.externalAuditRecords || []} internalRecords={data.internalAuditRecords || []} /></div>}
          <ExternalAuditReportTable records={data.externalAuditRecords || []} isEditable={false} />
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Internal Audit Report</h3>
          <InternalAuditReportTable records={data.internalAuditRecords || []} isEditable={false} />
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-800">Parameters of Fire Safety</h3>
            <button onClick={() => setShowParamsChart(!showParamsChart)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-xs font-medium">
              {showParamsChart ? <><EyeOff className="w-3.5 h-3.5" />Hide Chart</> : <><Eye className="w-3.5 h-3.5" />Show Chart</>}
            </button>
          </div>
          {showParamsChart && <div className="mb-4 border-t pt-4"><FireSafetyParametersChart parameters={data.fireSafetyParameters || []} reportName={data.name} reportDate={data.date} premisesName={data.premisesName} /></div>}
          <FireSafetyParametersTable data={data.fireSafetyParameters || []} isEditable={false} />
        </div>
      </div>
    </div>
  );
};

// ─── 2. MIS Checklist ─────────────────────────────────────────────────────────
const MISChecklistView: React.FC<{ id: string; onClose: () => void }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance.get(`/api/fire-safety-mis-checklists/${id}`)
      .then(r => setData(r.data?.data || r.data))
      .catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownload = () => {
    if (!data) return;
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(18); doc.setTextColor(0, 168, 165);
      doc.text("Fire Safety MIS Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      doc.text(`Name: ${data.name || "N/A"}`, 14, 26);
      doc.text(`Premises: ${data.premisesName || "N/A"}`, 14, 32);
      doc.text(`Date: ${data.date ? fmt(data.date) : "N/A"}`, 14, 38);
      doc.text(`Status Availability: ${data.statusAvailability || "N/A"}`, 14, 44);

      let y = 52;
      const fls: FLSSafetySystemRow[] = data.fireAndLifeSafetyEquipment || [];
      if (fls.length > 0) {
        doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(0,100,200);
        doc.rect(14, y - 5, doc.internal.pageSize.width - 28, 8, "F");
        doc.text("(I) Fire and Life Safety Equipment", 16, y); y += 4;
        autoTable(doc, {
          startY: y,
          head: [["Sr.No","Particulars","Installed","Working","Not Working","Last Inspection","Remarks"]],
          body: fls.map(r => [r.srNo, r.particulars, r.installed ?? "N/A", r.working ?? "N/A", r.notWorking ?? "N/A", r.lastInspectionDate || "N/A", r.remarks || "—"]),
          styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [0,100,200] },
          columnStyles: { 0:{cellWidth:10}, 1:{cellWidth:80} },
          didDrawPage: () => { y = 20; },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      const drills: FireIncidentDrillsRow[] = data.fireIncidentDrills || [];
      if (drills.length > 0) {
        doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(200,80,0);
        doc.rect(14, y - 5, doc.internal.pageSize.width - 28, 8, "F");
        doc.text("Fire Incident Drills & Training", 16, y); y += 4;
        autoTable(doc, {
          startY: y,
          head: [["Sr.No","Systems","Actual No","Target No","Status","Remarks"]],
          body: drills.map(r => [r.srNo, r.systems, r.actualNo ?? "N/A", r.targetNo ?? "N/A", r.status || "N/A", r.completionDateOrRemark || "—"]),
          styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [200,80,0] },
          didDrawPage: () => { y = 20; },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      const incidents: IncidentSummaryRow[] = data.incidentSummary || [];
      if (incidents.length > 0) {
        doc.setFontSize(11); doc.setTextColor(255,255,255); doc.setFillColor(150,0,0);
        doc.rect(14, y - 5, doc.internal.pageSize.width - 28, 8, "F");
        doc.text("Incident Summary", 16, y); y += 4;
        autoTable(doc, {
          startY: y,
          head: [["Description","Count","Remarks"]],
          body: incidents.map(r => [r.description, r.count ?? "N/A", r.remarks || "—"]),
          styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [150,0,0] },
          didDrawPage: () => { y = 20; },
        });
      }

      doc.save(`MIS_Report_${(data.name || "Checklist").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
    } catch { alert("Failed to generate PDF."); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><DotLoader /></div>;
  if (error || !data) return <div className="p-6 text-red-600 text-sm">{error || "No data"}</div>;

  const fls: FLSSafetySystemRow[] = data.fireAndLifeSafetyEquipment || [];
  const mep: MEPFLSInfrastructureRow[] = data.fireAndLifeSafetySystem || [];
  const drills: FireIncidentDrillsRow[] = data.fireIncidentDrills || [];
  const ert: ERTSafetyEquipmentRow[] = data.ertSafetyEquipment || [];
  const incidents: IncidentSummaryRow[] = data.incidentSummary || [];

  return (
    <div className="w-full px-6 py-6">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{data.name || "MIS Checklist"}</h2>
          <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium shadow-sm">
            <Download className="w-4 h-4" />Download PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <InfoRow label="Premises" value={data.premisesName || "N/A"} />
          <InfoRow label="Date" value={data.date ? fmt(data.date) : "N/A"} />
          <InfoRow label="Status Availability" value={data.statusAvailability || "N/A"} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
          <InfoRow label="Fire License Date" value={data.fireLicenseDate ? fmt(data.fireLicenseDate) : "N/A"} />
          <InfoRow label="Fire NOC Issue Date" value={data.fireNocIssueDate ? fmt(data.fireNocIssueDate) : "N/A"} />
          <InfoRow label="Date of Renewal" value={data.dateOfRenewal ? fmt(data.dateOfRenewal) : "N/A"} />
        </div>
      </div>
      <div className="flex flex-col gap-5">
        {fls.length > 0 && <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100"><h3 className="text-base font-semibold text-gray-800 mb-4">FLS Safety Systems</h3><FLSSafetySystemsTable data={fls} isEditable={false} /></div>}
        {mep.length > 0 && <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100"><h3 className="text-base font-semibold text-gray-800 mb-4">MEP FLS Infrastructure</h3><MEPFLSInfrastructureTable data={mep} isEditable={false} /></div>}
        {drills.length > 0 && <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100"><h3 className="text-base font-semibold text-gray-800 mb-4">Fire Incident Drills</h3><FireIncidentDrillsTable data={drills} isEditable={false} /></div>}
        {ert.length > 0 && <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100"><h3 className="text-base font-semibold text-gray-800 mb-4">ERT Safety Equipment</h3><ERTSafetyEquipmentTable data={ert} isEditable={false} /></div>}
        {incidents.length > 0 && <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100"><h3 className="text-base font-semibold text-gray-800 mb-4">Incident Summary</h3><IncidentSummaryTable data={incidents} isEditable={false} /></div>}
      </div>
    </div>
  );
};

// ─── 3. Electrical Safety Scorecard ──────────────────────────────────────────
const ElectricalScorecardView: React.FC<{ id: string; onClose: () => void }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    axiosInstance.get(`/api/electrical-safety-scorecards/${id}`)
      .then(r => setData(r.data?.data || r.data))
      .catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const toggle = (sid: string) => setCollapsed(p => { const n = new Set(p); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });

  const handleDownload = () => {
    if (!data) return;
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const sections: ElectricalSafetySection[] = data.sections || [];
      const ts = sections.reduce((s, sec) => s + sec.totalScore, 0);
      const tm = sections.reduce((s, sec) => s + sec.totalMaxScore, 0);
      doc.setFontSize(18); doc.setTextColor(0,168,165);
      doc.text("Electrical Safety Scorecard Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(80,80,80);
      doc.text(`Name: ${data.name || "N/A"}`, 14, 26);
      doc.text(`Premises: ${data.premisesName || "N/A"}`, 14, 32);
      doc.text(`Date: ${data.date ? fmt(data.date) : "N/A"}`, 14, 38);
      doc.text(`Overall Score: ${ts.toFixed(2)} / ${tm} (${tm > 0 ? ((ts/tm)*100).toFixed(2) : 0}%)`, 14, 44);
      let startY = 52;
      sections.forEach(sec => {
        doc.setFontSize(10); doc.setTextColor(255,255,255); doc.setFillColor(0,100,180);
        doc.rect(14, startY - 5, doc.internal.pageSize.width - 28, 8, "F");
        doc.text(`${sec.name}  |  ${sec.totalScore.toFixed(2)} / ${sec.totalMaxScore} (${sec.percentage.toFixed(2)}%)`, 16, startY);
        startY += 4;
        autoTable(doc, {
          startY,
          head: [["#","Description","Status","Score","Max","Remarks"]],
          body: sec.items.map((it, i) => [i+1, it.description, it.complianceStatus || "N/A", it.score.toFixed(2), it.maxScore, it.remarks || "—"]),
          styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [0,168,165] },
          columnStyles: { 0:{cellWidth:8}, 1:{cellWidth:90}, 2:{cellWidth:30}, 3:{cellWidth:15}, 4:{cellWidth:15} },
          didDrawPage: () => { startY = 20; },
        });
        startY = (doc as any).lastAutoTable.finalY + 8;
      });
      doc.save(`Electrical_Scorecard_${(data.name || "Report").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
    } catch { alert("Failed to generate PDF."); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><DotLoader /></div>;
  if (error || !data) return <div className="p-6 text-red-600 text-sm">{error || "No data"}</div>;

  const sections: ElectricalSafetySection[] = data.sections || [];
  const ts = sections.reduce((s, sec) => s + sec.totalScore, 0);
  const tm = sections.reduce((s, sec) => s + sec.totalMaxScore, 0);
  const pct = tm > 0 ? (ts / tm) * 100 : 0;

  return (
    <div className="w-full px-6 py-6">
      <div className="bg-white rounded-xl shadow-md p-6 mb-5 border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{data.name || "Electrical Safety Scorecard"}</h2>
          <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium shadow-sm">
            <Download className="w-4 h-4" />Download PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow label="Premises" value={data.premisesName || "N/A"} />
          <InfoRow label="Date" value={data.date ? fmt(data.date) : "N/A"} />
          <InfoRow label="Status" value={data.status || "N/A"} />
        </div>
      </div>
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-5 mb-5 text-white flex items-center gap-8">
        <div className="text-center"><div className="text-3xl font-bold">{ts.toFixed(2)}</div><div className="text-xs opacity-75 mt-1">Total Score</div></div>
        <div className="text-2xl opacity-40">/</div>
        <div className="text-center"><div className="text-3xl font-bold">{tm.toFixed(2)}</div><div className="text-xs opacity-75 mt-1">Max Score</div></div>
        <div className="ml-auto text-center"><div className="text-4xl font-bold">{pct.toFixed(1)}%</div><div className="text-xs opacity-75 mt-1">Compliance</div></div>
      </div>
      <div className="bg-white rounded-xl shadow-md p-5 mb-5 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-800">Section Scores Chart</h3>
          <button onClick={() => setShowChart(!showChart)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-xs font-medium">
            {showChart ? <><EyeOff className="w-3.5 h-3.5" />Hide</> : <><Eye className="w-3.5 h-3.5" />Show Chart</>}
          </button>
        </div>
        {showChart && sections.length > 0 && <ElectricalSafetyChart sections={sections} reportName={data.name} reportDate={data.date} premisesName={data.premisesName} />}
      </div>
      <div className="flex flex-col gap-4">
        {sections.map(sec => (
          <ElectricalSafetyTable key={sec.id} section={sec} isEditable={false} isCollapsed={collapsed.has(sec.id)} onToggleCollapse={() => toggle(sec.id)} />
        ))}
      </div>
    </div>
  );
};

// ─── 4. Split AC Checklist ────────────────────────────────────────────────────
const SplitACView: React.FC<{ id: string; onClose: () => void }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");
  const { allPremises, fetchPremises } = usePremisesStore();

  useEffect(() => { fetchPremises(); }, [fetchPremises]);
  useEffect(() => {
    axiosInstance.get(`/api/split-ac-checklist/${id}`)
      .then(r => setData(r.data?.data || r.data))
      .catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const doDownload = () => {
    if (!data) return;
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text("Split AC Maintenance Report", 14, 22);
    doc.setFontSize(10);
    const prem = allPremises.find((p: any) => p._id === (data.premisesId || data.premise));
    doc.text(`Checklist Name: ${data.name || "N/A"}`, 14, 32);
    doc.text(`Premise: ${prem ? prem.premisesName : (data.premisesName || "N/A")}`, 14, 38);
    doc.text(`Date: ${data.date ? new Date(data.date).toLocaleDateString() : "N/A"}`, 14, 44);
    if (comment) doc.text(`Comments: ${comment}`, 14, 50);
    const items: SplitACChecklistRow[] = data.items || [];
    autoTable(doc, {
      head: [["Sr. No.","Particulars","Status","Remarks"]],
      body: items.map((it: SplitACChecklistRow) => [it.srNo.toString(), it.particulars, it.status || "N/A", it.remarks || "N/A"]),
      startY: comment ? 58 : 52,
      styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [0,168,165] },
      columnStyles: { 0:{cellWidth:15}, 1:{cellWidth:80}, 2:{cellWidth:30} },
    });
    doc.save(`Split_AC_Report_${(data.name || "Checklist").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
    setShowModal(false); setComment("");
  };

  if (loading) return <div className="flex justify-center items-center h-64"><DotLoader /></div>;
  if (error || !data) return <div className="p-6 text-red-600 text-sm">{error || "No data"}</div>;

  return (
    <div className="w-full px-6 py-6">
      <div className="bg-white rounded-xl shadow-md p-6 mb-5 border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{data.name || "Split AC Checklist"}</h2>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium shadow-sm">
            <Download className="w-4 h-4" />Download PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow label="Premises" value={data.premisesName || "N/A"} />
          <InfoRow label="Date" value={data.date ? fmt(data.date) : "N/A"} />
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <SplitACChecklistTable data={data.items || []} onChange={() => {}} isViewMode={true} />
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Download Report</h3>
            <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] mb-5 min-h-[90px] text-sm" placeholder="Optional comment..." value={comment} onChange={e => setComment(e.target.value)} />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowModal(false); setComment(""); }} className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm">Cancel</button>
              <button onClick={doDownload} className="px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium">Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── 5. KPI Assessment ────────────────────────────────────────────────────────
type KPIStatus = "Complied"|"Partially Complied"|"Not Complied"|"Not Applicable"|""|null|undefined;
interface KPIItem { srNo: number; section: string; question: string; status: KPIStatus; remarks: string; }

const isNA = (s: KPIStatus) => !s || s === "Not Applicable" || (s as string) === "";
const kpiStats = (items: KPIItem[]) => {
  const t = items.length;
  const c = items.filter(i => i.status === "Complied").length;
  const p = items.filter(i => i.status === "Partially Complied").length;
  const n = items.filter(i => i.status === "Not Complied").length;
  const x = items.filter(i => isNA(i.status)).length;
  const pct = (v: number) => t ? Math.round((v/t)*100) : 0;
  return { t, c, p, n, x, cPct: pct(c), pPct: pct(p), nPct: pct(n), xPct: pct(x) };
};
const kpiPill = (s?: KPIStatus) => {
  if (s === "Complied") return "bg-green-100 text-green-800 border-green-200";
  if (s === "Partially Complied") return "bg-yellow-100 text-yellow-800 border-yellow-200";
  if (s === "Not Complied") return "bg-red-100 text-red-800 border-red-200";
  return "bg-gray-100 text-gray-600 border-gray-200";
};

const KPIAssessmentView: React.FC<{ id: string; onClose: () => void }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<any>(null);
  const [items, setItems] = useState<KPIItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const { premiseMap } = usePremisesStore();

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`/api/kpi/assessments/${id}`),
      axiosInstance.get(`/api/kpi/assessments/${id}/items`),
    ]).then(([ar, ir]) => {
      setAssessment(ar.data?.data || ar.data);
      const raw = ir.data?.data || ir.data || [];
      setItems((Array.isArray(raw) ? raw : []).map((it: any) => ({
        srNo: it.srNo, section: it.section || "General", question: it.question,
        status: (it.status || "") as KPIStatus, remarks: it.remarks || "",
      })));
    }).catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const grouped = useMemo(() => {
    const by: Record<string, KPIItem[]> = {};
    items.forEach(it => { const k = it.section || "General"; if (!by[k]) by[k] = []; by[k].push(it); });
    Object.keys(by).forEach(k => by[k].sort((a,b) => a.srNo - b.srNo));
    return Object.keys(by).map(name => ({ name, items: by[name] }));
  }, [items]);

  const stats = useMemo(() => kpiStats(items), [items]);
  const C = 2 * Math.PI * 40;
  const segs = useMemo(() => {
    const t = stats.t || 1;
    return [
      { key:"c", label:"Complied", count:stats.c, pct:(stats.c/t)*100, color:"#10b981" },
      { key:"p", label:"Partially Complied", count:stats.p, pct:(stats.p/t)*100, color:"#f59e0b" },
      { key:"n", label:"Not Complied", count:stats.n, pct:(stats.n/t)*100, color:"#ef4444" },
      { key:"x", label:"Pending / N/A", count:stats.x, pct:(stats.x/t)*100, color:"#9ca3af" },
    ];
  }, [stats]);

  const toggle = (name: string) => setCollapsed(p => { const n = new Set(p); n.has(name) ? n.delete(name) : n.add(name); return n; });

  const handleDownload = () => {
    if (!assessment) return;
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const premiseName = premiseMap[assessment.premiseId as string] || "N/A";
      doc.setFontSize(18); doc.setTextColor(0,168,165);
      doc.text("KPI Assessment Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(80,80,80);
      doc.text(`Assessment: ${assessment.name || "N/A"}`, 14, 26);
      doc.text(`Premise: ${premiseName}`, 14, 32);
      doc.text(`Date: ${assessment.date || "N/A"}`, 14, 38);
      doc.text(`Status: ${String(assessment.status || "N/A").toUpperCase()}`, 14, 44);
      doc.text(`Complied: ${stats.cPct}%  |  Partially: ${stats.pPct}%  |  Not Complied: ${stats.nPct}%  |  Pending/N/A: ${stats.xPct}%`, 14, 50);
      let startY = 58;
      grouped.forEach(sec => {
        doc.setFontSize(10); doc.setTextColor(255,255,255); doc.setFillColor(30,64,175);
        doc.rect(14, startY - 5, doc.internal.pageSize.width - 28, 8, "F");
        doc.text(sec.name, 16, startY); startY += 4;
        autoTable(doc, {
          startY,
          head: [["Sr.No","Question","Status","Remarks"]],
          body: sec.items.map(it => [it.srNo, it.question, it.status || "Pending", it.remarks || "—"]),
          styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [0,168,165] },
          columnStyles: { 0:{cellWidth:12}, 1:{cellWidth:120}, 2:{cellWidth:35} },
          didDrawPage: () => { startY = 20; },
        });
        startY = (doc as any).lastAutoTable.finalY + 8;
      });
      doc.save(`KPI_Assessment_${(assessment.name || "Report").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
    } catch { alert("Failed to generate PDF."); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><DotLoader /></div>;
  if (error || !assessment) return <div className="p-6 text-red-600 text-sm">{error || "No data"}</div>;

  let offset = 0;
  const donut = segs.map(seg => {
    const dash = (seg.pct / 100) * C;
    const el = <circle key={seg.key} cx="50" cy="50" r="40" fill="none" stroke={seg.color} strokeWidth="18" strokeDasharray={`${dash} ${C-dash}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)" />;
    offset += dash;
    return el;
  });

  return (
    <div className="w-full px-6 py-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-5 border border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{assessment.name || "KPI Assessment"}</h2>
          <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] text-sm font-medium shadow-sm">
            <Download className="w-4 h-4" />Download PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoRow label="Premise" value={premiseMap[assessment.premiseId as string] || "N/A"} />
          <InfoRow label="Date" value={assessment.date ? fmt(assessment.date) : "N/A"} />
          <InfoRow label="Status" value={String(assessment.status || "N/A").toUpperCase()} />
          <InfoRow label="Total Questions" value={String(stats.t)} />
        </div>
      </div>

      {/* Stats + Donut in one card */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-5 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-5">Compliance Overview</h3>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Donut */}
          <div className="flex-shrink-0 relative">
            <svg viewBox="0 0 120 120" className="w-44 h-44">
              <circle cx="60" cy="60" r="48" fill="none" stroke="#f3f4f6" strokeWidth="20" />
              {(() => {
                let off = 0;
                const circ = 2 * Math.PI * 48;
                return segs.map(seg => {
                  const dash = (seg.pct / 100) * circ;
                  const el = <circle key={seg.key} cx="60" cy="60" r="48" fill="none" stroke={seg.color} strokeWidth="20" strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-off} transform="rotate(-90 60 60)" strokeLinecap="round" />;
                  off += dash;
                  return el;
                });
              })()}
              <text x="60" y="55" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111827">{stats.cPct}%</text>
              <text x="60" y="70" textAnchor="middle" fontSize="9" fill="#6b7280">Complied</text>
            </svg>
          </div>
          {/* Stats grid */}
          <div className="flex-1 grid grid-cols-2 gap-3 w-full">
            {[
              { label: "Complied", v: stats.cPct, c: stats.c, color: "#10b981", bg: "bg-green-50", border: "border-green-200", text: "text-green-700" },
              { label: "Partially Complied", v: stats.pPct, c: stats.p, color: "#f59e0b", bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700" },
              { label: "Not Complied", v: stats.nPct, c: stats.n, color: "#ef4444", bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
              { label: "Pending / N/A", v: stats.xPct, c: stats.x, color: "#9ca3af", bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600" },
            ].map(card => (
              <div key={card.label} className={`rounded-xl border ${card.border} ${card.bg} p-4 flex items-center gap-3`}>
                <div className="w-3 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: card.color }} />
                <div>
                  <div className={`text-2xl font-bold ${card.text}`}>{card.v}%</div>
                  <div className={`text-xs font-semibold ${card.text} mt-0.5`}>{card.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{card.c} of {stats.t} items</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Overall progress bar */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>Overall compliance distribution</span>
            <span className="font-semibold text-gray-700">{stats.t} total questions</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-gray-100 flex">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${stats.cPct}%` }} title={`Complied: ${stats.cPct}%`} />
            <div className="h-full bg-yellow-400 transition-all" style={{ width: `${stats.pPct}%` }} title={`Partially: ${stats.pPct}%`} />
            <div className="h-full bg-red-500 transition-all" style={{ width: `${stats.nPct}%` }} title={`Not Complied: ${stats.nPct}%`} />
            <div className="h-full bg-gray-300 transition-all" style={{ width: `${stats.xPct}%` }} title={`Pending/N/A: ${stats.xPct}%`} />
          </div>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {segs.map(seg => (
              <div key={seg.key} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                <span className="text-xs text-gray-500">{seg.label} ({seg.pct.toFixed(1)}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section breakdown */}
      <div className="flex flex-col gap-3">
        {grouped.map(group => {
          const gs = kpiStats(group.items);
          const isCol = collapsed.has(group.name);
          return (
            <div key={group.name} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <button onClick={() => toggle(group.name)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-1.5 h-8 rounded-full bg-[#00A8A5] flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="font-semibold text-gray-800 text-sm block truncate">{group.name}</span>
                    <span className="text-xs text-gray-400">{group.items.length} questions · {gs.cPct}% complied</span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3 flex-1 mx-6">
                  <div className="flex-1 h-2 rounded-full overflow-hidden bg-gray-100 flex">
                    <div className="h-full bg-green-500" style={{ width: `${gs.cPct}%` }} />
                    <div className="h-full bg-yellow-400" style={{ width: `${gs.pPct}%` }} />
                    <div className="h-full bg-red-500" style={{ width: `${gs.nPct}%` }} />
                    <div className="h-full bg-gray-300" style={{ width: `${gs.xPct}%` }} />
                  </div>
                  <div className="flex gap-2 text-xs text-gray-500 whitespace-nowrap">
                    <span className="text-green-600 font-medium">{gs.c}✓</span>
                    <span className="text-yellow-600 font-medium">{gs.p}~</span>
                    <span className="text-red-600 font-medium">{gs.n}✗</span>
                  </div>
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isCol ? "" : "rotate-180"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {!isCol && (
                <div className="divide-y divide-gray-50 border-t border-gray-100">
                  {group.items.map((item, idx) => (
                    <div key={item.srNo} className={`px-5 py-3 flex flex-col md:flex-row md:items-start gap-3 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                      <span className="text-xs font-bold text-gray-300 w-8 flex-shrink-0 pt-0.5 text-right">{item.srNo}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 leading-relaxed">{item.question}</p>
                        {item.remarks && (
                          <p className="text-xs text-gray-400 mt-1 italic bg-gray-100 rounded px-2 py-1 inline-block">
                            💬 {item.remarks}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ${kpiPill(item.status)}`}>
                        {item.status || "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Detail Overlay (no Router nesting) ──────────────────────────────────────
interface OverlayProps { recordId: string; reportType: string; reportTypeLabel: string; onClose: () => void; }

const DetailOverlay: React.FC<OverlayProps> = ({ recordId, reportType, reportTypeLabel, onClose }) => {
  const viewer = () => {
    switch (reportType) {
      case "fire-safety-scorecard": return <FireSafetyScorecardView id={recordId} onClose={onClose} />;
      case "mis-reports": return <MISChecklistView id={recordId} onClose={onClose} />;
      case "electrical-scorecard": return <ElectricalScorecardView id={recordId} onClose={onClose} />;
      case "split-ac": return <SplitACView id={recordId} onClose={onClose} />;
      case "kpi-scorecard": return <KPIAssessmentView id={recordId} onClose={onClose} />;
      default: return <div className="flex items-center justify-center h-64 text-gray-500">Unknown report type</div>;
    }
  };
  return (
    <div className="w-full px-6 mt-20 pb-10">
    {/* // "fixed inset-0 z-50 bg-black/60 flex flex-col"> */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />Back to list
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-800">{reportTypeLabel} — View Mode</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-gray-50">{viewer()}</div>
    </div>
  );
};

// ─── ReportViewer (main list view) ───────────────────────────────────────────
const ReportViewer: React.FC = () => {
  const { premiseId = "", month = "", reportType = "" } = useParams<{ premiseId: string; month: string; reportType: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const premiseName = getPremiseName(premiseId, allPremises);
  const monthName = getMonthName(Number(month));
  const reportConfig = getReportTypeConfig(reportType);
  const year = searchParams.get("year") || "";
  const yearParam = year ? `?year=${year}` : "";

  useEffect(() => { if (!allPremises.length) fetchPremises(); }, []);

  useEffect(() => {
    if (!reportConfig) return;
    setLoading(true); setError(null);
    axiosInstance.get(reportConfig.apiEndpoint, { params: { premisesId: premiseId, month, ...(year ? { year } : {}) } })
      .then(r => { const d = r.data?.data || r.data || []; setRecords(Array.isArray(d) ? d : []); })
      .catch(e => { setError(e.response?.data?.error || e.response?.data?.message || "Failed to fetch"); if ([404,500].includes(e.response?.status)) setRecords([]); })
      .finally(() => setLoading(false));
  }, [premiseId, month, reportType, year]);

  // Quick download from table row — fetches full record and generates PDF
  const handleQuickDownload = useCallback(async (rid: string) => {
    if (!reportConfig || downloadingId) return;
    setDownloadingId(rid);
    try {
      const endpoint = reportConfig.apiEndpoint;
      const res = await axiosInstance.get(`${endpoint}/${rid}`);
      const d = res.data?.data || res.data;
      if (!d) { alert("No data found for this record."); return; }

      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(18); doc.setTextColor(0, 168, 165);
      doc.text(`${reportConfig.label} Report`, 14, 18);
      doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      doc.text(`Name: ${d.name || "N/A"}`, 14, 26);
      doc.text(`Premises: ${d.premisesName || "N/A"}`, 14, 32);
      doc.text(`Date: ${d.date ? fmt(d.date) : "N/A"}`, 14, 38);

      let y = 46;

      if (reportType === "split-ac") {
        const items: SplitACChecklistRow[] = d.items || [];
        autoTable(doc, {
          startY: y,
          head: [["Sr. No.", "Particulars", "Status", "Remarks"]],
          body: items.map((it: SplitACChecklistRow) => [it.srNo, it.particulars, it.status || "N/A", it.remarks || "—"]),
          styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [0, 168, 165] },
          columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: 90 }, 2: { cellWidth: 30 } },
        });
      } else if (reportType === "electrical-scorecard") {
        const sections: ElectricalSafetySection[] = d.sections || [];
        const ts = sections.reduce((s, sec) => s + sec.totalScore, 0);
        const tm = sections.reduce((s, sec) => s + sec.totalMaxScore, 0);
        doc.text(`Overall Score: ${ts.toFixed(2)} / ${tm} (${tm > 0 ? ((ts / tm) * 100).toFixed(2) : 0}%)`, 14, y); y += 8;
        sections.forEach(sec => {
          doc.setFontSize(9); doc.setTextColor(255, 255, 255); doc.setFillColor(0, 100, 180);
          doc.rect(14, y - 4, doc.internal.pageSize.width - 28, 7, "F");
          doc.text(`${sec.name} — ${sec.totalScore.toFixed(2)}/${sec.totalMaxScore}`, 16, y); y += 4;
          autoTable(doc, {
            startY: y,
            head: [["#", "Description", "Status", "Score", "Max", "Remarks"]],
            body: sec.items.map((it, i) => [i + 1, it.description, it.complianceStatus || "N/A", it.score.toFixed(2), it.maxScore, it.remarks || "—"]),
            styles: { fontSize: 6, cellPadding: 1.5 }, headStyles: { fillColor: [0, 168, 165] },
            columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 85 }, 2: { cellWidth: 28 }, 3: { cellWidth: 14 }, 4: { cellWidth: 14 } },
            didDrawPage: () => { y = 20; },
          });
          y = (doc as any).lastAutoTable.finalY + 6;
        });
      } else if (reportType === "fire-safety-scorecard") {
        const ext = d.externalAuditRecords || [];
        const int_ = d.internalAuditRecords || [];
        const params = d.fireSafetyParameters || [];
        if (ext.length) {
          autoTable(doc, { startY: y, head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Score", "Max"]], body: ext.map((r: any) => [`Cat ${r.category}`, r.totalNC ?? "N/A", r.completionNC ?? "N/A", r.pending ?? "N/A", r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A", r.currentScore != null ? r.currentScore.toFixed(2) : "N/A", r.maxScore]), styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [255, 100, 0] } });
          y = (doc as any).lastAutoTable.finalY + 6;
        }
        if (int_.length) {
          autoTable(doc, { startY: y, head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Score", "Max"]], body: int_.map((r: any) => [`Cat ${r.category}`, r.totalNC ?? "N/A", r.completionNC ?? "N/A", r.pending ?? "N/A", r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A", r.currentScore != null ? r.currentScore.toFixed(2) : "N/A", r.maxScore]), styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [180, 0, 120] } });
          y = (doc as any).lastAutoTable.finalY + 6;
        }
        if (params.length) {
          const ts = params.reduce((s: number, p: any) => s + (p.score ?? 0), 0);
          const tm = params.reduce((s: number, p: any) => s + p.maxScore, 0);
          autoTable(doc, { startY: y, head: [["Parameter", "Score", "Max Score", "Percentage"]], body: [...params.map((p: any) => [p.parameter, p.score != null ? p.score.toFixed(2) : "N/A", p.maxScore.toFixed(2), p.percentage != null ? `${p.percentage.toFixed(2)}%` : "N/A"]), ["TOTAL", ts.toFixed(2), tm.toFixed(2), tm > 0 ? `${((ts / tm) * 100).toFixed(2)}%` : "N/A"]], styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [0, 168, 165] }, didParseCell: (data: any) => { if (data.row.index === params.length) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [255, 235, 150]; } } });
        }
      } else if (reportType === "mis-reports") {
        const fls: FLSSafetySystemRow[] = d.fireAndLifeSafetyEquipment || [];
        if (fls.length) {
          autoTable(doc, { startY: y, head: [["Sr.No", "Particulars", "Installed", "Working", "Not Working", "Last Inspection", "Remarks"]], body: fls.map(r => [r.srNo, r.particulars, r.installed ?? "N/A", r.working ?? "N/A", r.notWorking ?? "N/A", r.lastInspectionDate || "N/A", r.remarks || "—"]), styles: { fontSize: 6, cellPadding: 1.5 }, headStyles: { fillColor: [0, 100, 200] }, columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 80 } } });
          y = (doc as any).lastAutoTable.finalY + 6;
        }
        const incidents: IncidentSummaryRow[] = d.incidentSummary || [];
        if (incidents.length) {
          autoTable(doc, { startY: y, head: [["Description", "Count", "Remarks"]], body: incidents.map(r => [r.description, r.count ?? "N/A", r.remarks || "—"]), styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [150, 0, 0] } });
        }
      } else if (reportType === "kpi-scorecard") {
        // KPI items
        const itemsRes = await axiosInstance.get(`/api/kpi/assessments/${rid}/items`);
        const kpiItems = itemsRes.data?.data || itemsRes.data || [];
        const by: Record<string, any[]> = {};
        kpiItems.forEach((it: any) => { const k = it.section || "General"; if (!by[k]) by[k] = []; by[k].push(it); });
        for (const [sec, sitems] of Object.entries(by)) {
          doc.setFontSize(9); doc.setTextColor(255, 255, 255); doc.setFillColor(30, 64, 175);
          doc.rect(14, y - 4, doc.internal.pageSize.width - 28, 7, "F");
          doc.text(sec, 16, y); y += 4;
          autoTable(doc, { startY: y, head: [["Sr.No", "Question", "Status", "Remarks"]], body: sitems.map((it: any) => [it.srNo, it.question, it.status || "Pending", it.remarks || "—"]), styles: { fontSize: 6, cellPadding: 1.5 }, headStyles: { fillColor: [0, 168, 165] }, columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: 120 }, 2: { cellWidth: 30 } }, didDrawPage: () => { y = 20; } });
          y = (doc as any).lastAutoTable.finalY + 6;
        }
      }

      doc.save(`${reportConfig.label.replace(/\s+/g, "_")}_${(d.name || rid).replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download PDF.");
    } finally {
      setDownloadingId(null);
    }
  }, [reportConfig, reportType, downloadingId]);

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: "name", headerName: "Name", flex: 2, minWidth: 180,
      cellStyle: { padding: "10px 12px", display: "flex", alignItems: "center" } as any,
      cellRenderer: (p: ICellRendererParams) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900 text-sm truncate">{p.value || "N/A"}</span>
        </div>
      ),
    },
    {
      field: "premisesName", headerName: "Premises", flex: 1.5, minWidth: 140,
      cellStyle: { padding: "10px 12px", display: "flex", alignItems: "center" } as any,
      cellRenderer: (p: ICellRendererParams) => (
        <div className="flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-[#00A8A5] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-gray-600 text-sm truncate">{p.value || "N/A"}</span>
        </div>
      ),
    },
    {
      field: "date", headerName: "Date", flex: 1, minWidth: 120,
      cellStyle: { padding: "10px 12px", display: "flex", alignItems: "center" } as any,
      cellRenderer: (p: ICellRendererParams) => (
        <div className="flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-gray-600 text-sm">{p.value ? fmt(p.value) : "N/A"}</span>
        </div>
      ),
    },
    {
      field: "status", headerName: "Status", flex: 1, minWidth: 120,
      cellStyle: { padding: "10px 12px", display: "flex", alignItems: "center" } as any,
      cellRenderer: (p: ICellRendererParams) => <StatusBadge status={p.value} />,
    },
    {
      field: "action", headerName: "Actions", flex: 1.5, minWidth: 200, sortable: false,
      cellStyle: { padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" } as any,
      cellRenderer: (p: ICellRendererParams) => {
        const rid = p.data?._id || p.data?.id;
        const isDownloading = downloadingId === rid;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedId(rid)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#00A8A5] rounded-lg hover:bg-[#008A87] transition-colors shadow-sm"
              title="View full report"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View
            </button>
            <button
              onClick={() => handleQuickDownload(rid)}
              disabled={isDownloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download PDF"
            >
              {isDownloading ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              PDF
            </button>
          </div>
        );
      },
    },
  ], [downloadingId, handleQuickDownload]);

  const defaultColDef = useMemo(() => ({ sortable: true, resizable: true }), []);
  const onGridReady = useCallback((e: GridReadyEvent) => { e.api.sizeColumnsToFit(); }, []);

  if (!reportConfig) {
    return (
      <div className="w-full px-6 mt-20 pb-10 flex flex-col items-center justify-center py-24 text-center">
        <div className="bg-red-50 rounded-full p-5 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Unknown report type</h3>
        <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${reportType}`)} className="flex items-center gap-2 px-5 py-2.5 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] font-medium mt-4">
          <ArrowLeft className="w-4 h-4" />Back
        </button>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center items-center h-64 mt-20"><DotLoader /></div>;

  return (
    <>
      {!selectedId && <div className="w-full px-6 mt-20 pb-10">
        {/* Breadcrumb — new route: /premiseId/reportType/month */}
        <nav aria-label="breadcrumb" data-testid="breadcrumb" className="mb-4">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
            <li><Link to="/fire-system/monitoring" className="hover:text-[#00A8A5] font-medium">Monitoring</Link></li>
            <li className="text-gray-300 select-none">›</li>
            <li><Link to={`/fire-system/monitoring/${premiseId}`} className="hover:text-[#00A8A5] truncate max-w-[120px] inline-block">{premiseName}</Link></li>
            <li className="text-gray-300 select-none">›</li>
            <li><Link to={`/fire-system/monitoring/${premiseId}/${reportType}${yearParam}`} className="hover:text-[#00A8A5]">{reportConfig.label}</Link></li>
            <li className="text-gray-300 select-none">›</li>
            <li aria-current="page" className="text-gray-800 font-semibold">{monthName}</li>
          </ol>
        </nav>

        <button onClick={() => navigate(`/fire-system/monitoring/${premiseId}/${reportType}${yearParam}`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />Back
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{reportConfig.label}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Showing records for <span className="font-semibold text-gray-700">{premiseName}</span> — <span className="font-semibold text-gray-700">{monthName}</span>
            </p>
          </div>
          {records.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00A8A5]/10 text-[#00A8A5] text-sm font-semibold rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {records.length} record{records.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="bg-gray-100 rounded-full p-5 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No records found</h3>
              <p className="text-gray-400 text-sm max-w-xs">
                There are no {reportConfig.label} records for <span className="font-medium">{premiseName}</span> in <span className="font-medium">{monthName}</span>.
              </p>
            </div>
          ) : (
            <div className="ag-theme-quartz w-full" style={{ height: "calc(100vh - 340px)", minHeight: 400 }}>
              <AgGridReact
                theme={gridTheme}
                rowData={records}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                headerHeight={65}
                rowHeight={60}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                animateRows={true}
                onGridReady={onGridReady}
              />
            </div>
          )}
        </div>
      </div>
}
      {selectedId && reportConfig && (
        <DetailOverlay
          recordId={selectedId}
          reportType={reportType}
          reportTypeLabel={reportConfig.label}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
};

export default ReportViewer;
